import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../../utils/api/axiosInstance';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppStoreSelector } from '../../../store';
import { useToast } from '../../general/components/Toast.hook';

import LanguagesTable from './LanguagesTable';
import TranslationsTable from './TranslationsTable';
import OptionsModal from 'features/general/components/OptionsModal/OptionsModal';
import ProgressModal from 'features/general/components/ProgressModal/ProgressModal';
import type { OptionsInterface } from '../../general/components/OptionsModal/OptionsInterface';

import { useTheme } from '@mui/material/styles';

import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import FormGroup from '@mui/material/FormGroup'; 
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CssBaseline from '@mui/material/CssBaseline';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SyncIcon from '@mui/icons-material/Sync';
import ConstructionIcon from '@mui/icons-material/Construction';
import BackupIcon from '@mui/icons-material/Backup';
import InfoIcon from '@mui/icons-material/Info';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const HARDCODED_SCOPES = [
  {
    path: 'General/Languages',
    parentPath: 'General',
    searchTerms: ['languages', 'language']
  },
  {
    path: 'General/Translations',
    parentPath: 'General',
    searchTerms: ['translations', 'translation']
  }
];

const PROTECTED_JSON_ENTRIES = [
  {
    fieldKey: 'dam.presets',
    protectedEntryName: 'preview',
    editableFields: ['width', 'height', 'format']
  }
];

interface ConfigItem {
  configurationid: number;
  key: string;
  label_key: string;
  description_key?: string;
  scope_path: string;
  datatype: 'text' | 'string' | 'number' | 'selection' | 'multiselection' | 'checkbox' | 'json';
  default_value?: string;
  options_json?: string;
  is_translatable: boolean | number;
  ui_group: string;
  validation_schema?: string;
  sort_order: number;
  is_editable: boolean | number;
  parent_id?: number;
  parent_condition?: string;
  is_sensitive?: boolean | number;
}

interface Language {
  LanguageID: number;
  ParentID?: number;
  LangCode: string;
  LangName: string;
  IsDefault: number;
  created_by: string;
  updated_by: string;
  created_on: string;
  modified_on: string;
}

interface ScopeNode {
  name: string;
  path: string;
  children?: ScopeNode[];
  isLeaf: boolean;
}

// utils
function isFieldEditable(field: ConfigItem): boolean {
  return Boolean(field.is_editable) && field.is_editable !== 0;
}

function isFieldSensitive(field: ConfigItem): boolean {
  return Boolean(field.is_sensitive) && field.is_sensitive !== 0;
}

interface MultiLanguageInputProps {
  field: ConfigItem;
  control: any;
  errors: any;
  languages: Language[];
  sensitiveFieldsVisible: { [key: string]: boolean };
  toggleSensitiveFieldVisibility: (fieldId: string) => void;
  getFieldLabel: (labelKey: string) => string;
}

function MultiLanguageInput({
  field,
  control,
  errors,
  languages,
  sensitiveFieldsVisible,
  toggleSensitiveFieldVisibility,
  getFieldLabel
}: MultiLanguageInputProps) {
  const isSensitive = isFieldSensitive(field) && (field.datatype === 'text' || field.datatype === 'string');

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
        {getFieldLabel(field.label_key)}
      </Typography>
      {languages.map((language: any) => {
        const fieldName = `${field.configurationid}_lang_${language.LangCode}`;
        const isVisible = sensitiveFieldsVisible[fieldName] || !isSensitive;

        return (
          <Box key={language.LanguageID} sx={{ mb: 2 }}>
            <Controller
              name={fieldName}
              control={control}
              render={({ field: controllerField }) => (
                <TextField
                  {...controllerField}
                  value={controllerField.value || ''}
                  label={`${getFieldLabel(field.label_key)} (${language.LangCode})`}
                  disabled={!isFieldEditable(field)}
                  fullWidth
                  size="small"
                  type={isSensitive && !isVisible ? 'password' : 'text'}
                  error={!!(errors as any)[fieldName]}
                  helperText={(errors as any)[fieldName]?.message as string}
                  InputProps={{
                    endAdornment: isSensitive ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => toggleSensitiveFieldVisibility(fieldName)}
                          edge="end"
                          size="small"
                        >
                          {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              )}
            />
          </Box>
        );
      })}
    </Box>
  );
}

// Types for JSON schema
interface JsonSchemaKey {
  name: string;
  datatype: 'string' | 'number' | 'selection' | 'checkbox' | 'multiselection';
  label?: string;
  required?: boolean;
  options_json?: {
    type: 'options' | 'query';
    options?: Array<{ value: string; label: string }>;
    query?: string;
  };
}

interface JsonSchema {
  keys: JsonSchemaKey[];
  settings?: {
    max_entries?: number;
    display_mode?: 'cards' | 'table'; // cards = default horizontal layout, table = structured table view
  };
}

// table row component for table display mode
function JsonSchemaTableRow({
  entry,
  index,
  schema,
  onChange,
  onRemove,
  disabled,
  canRemove,
  fieldKey
}: {
  entry: Record<string, any>;
  index: number;
  schema: JsonSchema;
  onChange: (index: number, key: string, value: any) => void;
  onRemove: (index: number) => void;
  disabled: boolean;
  canRemove: boolean;
  fieldKey?: string;
}) {
  const { t } = useTranslation();
  
  // check if this entry is protected
  const protectionConfig = PROTECTED_JSON_ENTRIES.find(config => config.fieldKey === fieldKey);
  const isProtectedEntry = protectionConfig && entry.name === protectionConfig.protectedEntryName;
  
  // helper to check if a field value is empty
  const isFieldEmpty = (keyDef: JsonSchemaKey, value: any) => {
    if (keyDef.datatype === 'multiselection') {
      return !value || (Array.isArray(value) && value.length === 0);
    }
    if (keyDef.datatype === 'checkbox') {
      return false; // checkboxes always have a value (true/false)
    }
    return value === '' || value === null || value === undefined;
  };

  return (
    <TableRow 
      sx={{ 
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        '&:nth-of-type(even)': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)'
        },
        ...(isProtectedEntry && {
          backgroundColor: 'rgba(33, 150, 243, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.12)'
          }
        })
      }}
    >
      {schema.keys.map((keyDef) => {
        const isFieldDisabled = disabled || (isProtectedEntry && protectionConfig && !protectionConfig.editableFields.includes(keyDef.name));
        const isEmpty = isFieldEmpty(keyDef, entry[keyDef.name]);
        const showError = keyDef.required && isEmpty;

        return (
          <TableCell 
            key={keyDef.name} 
            sx={{ 
              py: 1.5,
              px: 1,
              verticalAlign: 'middle',
              borderRight: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': {
                borderRight: 'none'
              }
            }}
          >
            {keyDef.datatype === 'string' && (
              <TextField
                value={entry[keyDef.name] || ''}
                onChange={(e) => onChange(index, keyDef.name, e.target.value)}
                disabled={isFieldDisabled}
                required={keyDef.required}
                error={showError}
                placeholder={`Enter ${keyDef.label || keyDef.name}`}
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'background.paper'
                  }
                }}
              />
            )}
            {keyDef.datatype === 'number' && (
              <TextField
                type="number"
                value={entry[keyDef.name] || ''}
                onChange={(e) => onChange(index, keyDef.name, e.target.value)}
                disabled={isFieldDisabled}
                required={keyDef.required}
                error={showError}
                placeholder="0"
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'background.paper'
                  }
                }}
              />
            )}
            {keyDef.datatype === 'selection' && keyDef.options_json?.options && (
              <FormControl fullWidth size="small" error={showError}>
                <Select
                  value={entry[keyDef.name] || ''}
                  onChange={(e) => onChange(index, keyDef.name, e.target.value)}
                  disabled={isFieldDisabled}
                  displayEmpty
                  sx={{
                    backgroundColor: 'background.paper'
                  }}
                >
                  {!keyDef.required && (
                    <MenuItem value="">
                      <em>{t('mdm.none')}</em>
                    </MenuItem>
                  )}
                  {(keyDef.options_json?.options || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {keyDef.datatype === 'checkbox' && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Checkbox
                  checked={!!entry[keyDef.name]}
                  onChange={(e) => onChange(index, keyDef.name, e.target.checked)}
                  disabled={isFieldDisabled}
                  size="small"
                />
              </Box>
            )}
            {keyDef.datatype === 'multiselection' && keyDef.options_json?.options && (
              <FormControl fullWidth size="small" error={showError}>
                <Select
                  multiple
                  value={Array.isArray(entry[keyDef.name]) ? entry[keyDef.name] : []}
                  onChange={(e) => {
                    const newValue = e.target.value as string[];
                    const previousValue = Array.isArray(entry[keyDef.name]) ? entry[keyDef.name] : [];
                    
                    const allJustSelected = newValue.includes('*') && !previousValue.includes('*');
                    
                    let finalValue: string[];
                    
                    if (allJustSelected) {
                      finalValue = ['*'];
                    } else if (previousValue.includes('*') && newValue.length > 1) {
                      finalValue = newValue.filter(v => v !== '*');
                    } else {
                      finalValue = newValue;
                    }
                    
                    onChange(index, keyDef.name, finalValue);
                  }}
                  disabled={isFieldDisabled}
                  displayEmpty
                  sx={{
                    backgroundColor: 'background.paper'
                  }}
                  renderValue={(selected) => {
                    const selectedArray = selected as string[];
                    const options = keyDef.options_json?.options || [];
                    
                    if (selectedArray.length === 0) {
                      return <em style={{ color: '#999' }}>Select...</em>;
                    }
                    
                    if (selectedArray.includes('*')) {
                      const allOption = options.find(opt => opt.value === '*');
                      return allOption?.label || 'All (*)';
                    }
                    const labels = selectedArray.map(val => {
                      const option = options.find(opt => opt.value === val);
                      return option?.label || val;
                    });
                    return labels.join(', ');
                  }}
                >
                  {(keyDef.options_json?.options || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox 
                        checked={Array.isArray(entry[keyDef.name]) && entry[keyDef.name].includes(opt.value)}
                        size="small"
                      />
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </TableCell>
        );
      })}
      <TableCell 
        align="center"
        sx={{ 
          py: 1.5,
          px: 1,
          position: 'sticky',
          right: 0,
          backgroundColor: 'inherit',
          zIndex: 1
        }}
      >
        {canRemove && !isProtectedEntry ? (
          <Tooltip title={t('mdm.deleteEntry')} arrow>
            <IconButton 
              size="small" 
              onClick={() => onRemove(index)} 
              disabled={disabled} 
              color="error"
              sx={{ 
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.contrastText'
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : isProtectedEntry ? (
          <Tooltip title={t('mdm.protectedEntry')} arrow>
            <span>
              <IconButton 
                size="small" 
                disabled
                sx={{ visibility: 'visible' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function JsonSchemaEntry({
  entry,
  index,
  schema,
  onChange,
  onRemove,
  disabled,
  canRemove,
  fieldKey
}: {
  entry: Record<string, any>;
  index: number;
  schema: JsonSchema;
  onChange: (index: number, key: string, value: any) => void;
  onRemove: (index: number) => void;
  disabled: boolean;
  canRemove: boolean;
  fieldKey?: string;
}) {
  const { t } = useTranslation();
  
  // check if this entry is protected
  const protectionConfig = PROTECTED_JSON_ENTRIES.find(config => config.fieldKey === fieldKey);
  const isProtectedEntry = protectionConfig && entry.name === protectionConfig.protectedEntryName;
  
  // helper to check if a field value is empty
  const isFieldEmpty = (keyDef: JsonSchemaKey, value: any) => {
    if (keyDef.datatype === 'multiselection') {
      return !value || (Array.isArray(value) && value.length === 0);
    }
    if (keyDef.datatype === 'checkbox') {
      return false; // checkboxes always have a value (true/false)
    }
    return value === '' || value === null || value === undefined;
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
       {schema.keys.map((keyDef) => {
         // determine if this field should be disabled for protected entries
         const isFieldDisabled = disabled || (isProtectedEntry && protectionConfig && !protectionConfig.editableFields.includes(keyDef.name));
         const isEmpty = isFieldEmpty(keyDef, entry[keyDef.name]);
         const showError = keyDef.required && isEmpty;

        return (
          <Box key={keyDef.name} sx={{ flex: keyDef.name === 'pattern' ? 2 : 1 }}>
            {keyDef.datatype === 'string' && (
              <TextField
                label={keyDef.label || keyDef.name}
                value={entry[keyDef.name] || ''}
                onChange={(e) => onChange(index, keyDef.name, e.target.value)}
                disabled={isFieldDisabled}
                required={keyDef.required}
                error={showError}
                helperText={showError ? t('validation.required') : ''}
                fullWidth
                size="small"
              />
            )}
            {keyDef.datatype === 'number' && (
              <TextField
                label={keyDef.label || keyDef.name}
                type="number"
                value={entry[keyDef.name] || ''}
                onChange={(e) => onChange(index, keyDef.name, e.target.value)}
                disabled={isFieldDisabled}
                required={keyDef.required}
                error={showError}
                helperText={showError ? t('validation.required') : ''}
                fullWidth
                size="small"
              />
            )}
            {keyDef.datatype === 'selection' && keyDef.options_json?.options && (
              <FormControl fullWidth size="small" error={showError} required={keyDef.required}>
                <InputLabel>{keyDef.label || keyDef.name}</InputLabel>
                <Select
                  value={entry[keyDef.name] || ''}
                  label={keyDef.label || keyDef.name}
                  onChange={(e) => onChange(index, keyDef.name, e.target.value)}
                  disabled={isFieldDisabled}
                >
                  {!keyDef.required && (
                  <MenuItem value="">
                      <em>{t('mdm.none')}</em>
                  </MenuItem>
                  )}
                  {keyDef.options_json.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {showError && <FormHelperText>{t('validation.required')}</FormHelperText>}
              </FormControl>
            )}
            {keyDef.datatype === 'checkbox' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!entry[keyDef.name]}
                    onChange={(e) => onChange(index, keyDef.name, e.target.checked)}
                    disabled={isFieldDisabled}
                  />
                }
                label={keyDef.label || keyDef.name}
              />
            )}
            {keyDef.datatype === 'multiselection' && keyDef.options_json?.options && (
              <FormControl fullWidth size="small" error={showError} required={keyDef.required}>
                <InputLabel>{keyDef.label || keyDef.name}</InputLabel>
                <Select
                  multiple
                  value={Array.isArray(entry[keyDef.name]) ? entry[keyDef.name] : []}
                  label={keyDef.label || keyDef.name}
                  onChange={(e) => {
                    const newValue = e.target.value as string[];
                    const previousValue = Array.isArray(entry[keyDef.name]) ? entry[keyDef.name] : [];
                    
                    // check if "All (*)" was just selected
                    const allJustSelected = newValue.includes('*') && !previousValue.includes('*');
                    
                    let finalValue: string[];
                    
                    if (allJustSelected) {
                      // when "All (*)" is selected, only store "*" (it represents all)
                      finalValue = ['*'];
                    } else if (previousValue.includes('*') && newValue.length > 1) {
                      // if "All (*)" was selected and user clicks another option, deselect "All (*)"
                      finalValue = newValue.filter(v => v !== '*');
                    } else {
                      // normal multi-selection behavior
                      finalValue = newValue;
                    }
                    
                    onChange(index, keyDef.name, finalValue);
                  }}
                  disabled={isFieldDisabled}
                  renderValue={(selected) => {
                    const selectedArray = selected as string[];
                    const options = keyDef.options_json?.options || [];
                    
                    if (selectedArray.includes('*')) {
                      // find the label for "*" option
                      const allOption = options.find(opt => opt.value === '*');
                      return allOption?.label || 'All (*)';
                    }
                    // map values to their labels
                    const labels = selectedArray.map(val => {
                      const option = options.find(opt => opt.value === val);
                      return option?.label || val;
                    });
                    return labels.join(', ');
                  }}
                >
                  {(keyDef.options_json?.options || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox checked={Array.isArray(entry[keyDef.name]) && entry[keyDef.name].includes(opt.value)} />
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {showError && <FormHelperText>{t('validation.required')}</FormHelperText>}
              </FormControl>
            )}
          </Box>
        );
      })}
        <IconButton 
          size="small" 
          onClick={() => onRemove(index)} 
          disabled={disabled || !canRemove || isProtectedEntry} 
          color="error"
          sx={{ visibility: (canRemove && !isProtectedEntry) ? 'visible' : 'hidden' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

interface JsonFieldInputProps {
  field: ConfigItem;
  control: any;
  errors: any;
  getFieldLabel: (labelKey: string) => string;
}

function JsonFieldInput({
  field,
  control,
  errors,
  getFieldLabel
}: JsonFieldInputProps) {
  const { t } = useTranslation();
  const [queryData, setQueryData] = useState<any[]>([]);
  const [schema, setSchema] = useState<JsonSchema | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [responseType, setResponseType] = useState<'simple' | 'schema'>('simple');

  useEffect(() => {
    const executeQuery = async () => {
      if (!field.options_json) {
        setQueryError('No options_json defined');
        return;
      }

      try {
        setQueryLoading(true);
        setQueryError(null);
        
        const response = await axios.post('/api/mdm/config/executeOptionsQuery', {
          query: field.options_json
        });

        if (response.data && response.data.success) {
          setResponseType(response.data.type || 'simple');
          
          if (response.data.type === 'schema') {
            setSchema(response.data.data);
          } else {
            setQueryData(response.data.data || []);
          }
        } else {
          setQueryError('Invalid response from query execution');
        }
      } catch (err: any) {
        setQueryError(err.response?.data?.message || err.message || 'Failed to execute query');
      } finally {
        setQueryLoading(false);
      }
    };

    executeQuery();
  }, [field.options_json]);

  if (queryLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading data...</Typography>
      </Box>
    );
  }

  if (queryError) {
    return (
      <Alert severity="error" sx={{ mb: 1 }}>
        {queryError}
      </Alert>
    );
  }

  if (responseType === 'schema' && schema) {
    const maxEntries = schema.settings?.max_entries || 10;
    const displayMode = schema.settings?.display_mode || 'cards';

    return (
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
          {getFieldLabel(field.label_key)}
        </Typography>
        <Controller
          name={field.configurationid.toString()}
          control={control}
          render={({ field: controllerField }) => {
            // Parse current value
            let entries: Array<Record<string, any>> = [];
            try {
              const parsed = controllerField.value ? 
                (typeof controllerField.value === 'string' ? JSON.parse(controllerField.value) : controllerField.value) 
                : [];
              entries = Array.isArray(parsed) ? parsed : [parsed];
            } catch {
              entries = [];
            }

            // Ensure at least one entry for UI display
            if (entries.length === 0) {
              entries = [{}];
            }

            // Helper to check if an entry has any non-empty values
            const isEntryEmpty = (entry: Record<string, any>) => {
              return Object.values(entry).every(v => v === '' || v === null || v === undefined);
            };

            // Helper to filter empty entries for saving
            const filterEmptyEntries = (entriesToFilter: Array<Record<string, any>>) => {
              const filtered = entriesToFilter.filter(entry => !isEntryEmpty(entry));
              return filtered;
            };

            const handleEntryChange = (index: number, key: string, value: any) => {
              const newEntries = [...entries];
              newEntries[index] = { ...newEntries[index], [key]: value };
              const filteredEntries = filterEmptyEntries(newEntries);
              controllerField.onChange(filteredEntries.length > 0 ? JSON.stringify(filteredEntries) : '');
            };

            const handleAddEntry = () => {
              if (entries.length < maxEntries) {
                const newEntries = [...entries, {}];
                controllerField.onChange(JSON.stringify(newEntries));
              }
            };

            const handleRemoveEntry = (index: number) => {
              if (entries.length > 1) {
                const newEntries = entries.filter((_, i) => i !== index);
                const filteredEntries = filterEmptyEntries(newEntries);
                controllerField.onChange(filteredEntries.length > 0 ? JSON.stringify(filteredEntries) : '');
              }
            };

            // render based on display mode
            if (displayMode === 'table') {
              // table view
              return (
                <>
                  <TableContainer 
                    component={Paper} 
                    variant="outlined"
                    sx={{ 
                      maxHeight: 600,
                      '& .MuiTable-root': {
                        borderCollapse: 'separate',
                      }
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {schema.keys.map((keyDef) => (
                            <TableCell 
                              key={keyDef.name}
                              sx={{ 
                                fontWeight: 600,
                                backgroundColor: 'background.default',
                                borderBottom: 2,
                                borderColor: 'divider',
                                whiteSpace: 'nowrap',
                                minWidth: keyDef.datatype === 'multiselection' ? 200 : 
                                         keyDef.datatype === 'selection' ? 150 : 
                                         keyDef.datatype === 'checkbox' ? 100 : 120
                              }}
                            >
                              {keyDef.label || keyDef.name}
                              {keyDef.required && (
                                <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                              )}
                            </TableCell>
                          ))}
                          <TableCell 
                            align="center" 
                            sx={{ 
                              width: 80,
                              fontWeight: 600,
                              backgroundColor: 'background.default',
                              borderBottom: 2,
                              borderColor: 'divider',
                              position: 'sticky',
                              right: 0,
                              zIndex: 2
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entries.map((entry, index) => (
                          <JsonSchemaTableRow
                            key={index}
                            entry={entry}
                            index={index}
                            schema={schema}
                            onChange={handleEntryChange}
                            onRemove={handleRemoveEntry}
                            disabled={!isFieldEditable(field)}
                            canRemove={entries.length > 1}
                            fieldKey={field.key}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tooltip 
                      title={entries.length >= maxEntries ? t('mdm.maxEntriesReached', { max: maxEntries }) : t('mdm.addNewEntry')}
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={handleAddEntry}
                          disabled={!isFieldEditable(field) || entries.length >= maxEntries}
                          startIcon={<AddIcon />}
                          sx={{ 
                            textTransform: 'none',
                            fontWeight: 500,
                            boxShadow: 2,
                            '&:hover': {
                              boxShadow: 4
                            }
                          }}
                        >
                          {t('mdm.addEntry')}
                        </Button>
                      </span>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {entries.length} / {maxEntries}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('mdm.entries')}
                      </Typography>
                    </Box>
                  </Box>
                </>
              );
            }

            // default card view
            return (
              <>
                {entries.map((entry, index) => (
                  <JsonSchemaEntry
                    key={index}
                    entry={entry}
                    index={index}
                    schema={schema}
                    onChange={handleEntryChange}
                    onRemove={handleRemoveEntry}
                    disabled={!isFieldEditable(field)}
                    canRemove={entries.length > 1}
                    fieldKey={field.key}
                  />
                ))}
                {entries.length < maxEntries && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddEntry}
                    disabled={!isFieldEditable(field)}
                  >
                    + Add Entry ({entries.length}/{maxEntries})
                  </Button>
                )}
              </>
            );
          }}
        />
        <FormHelperText error={!!(errors as any)[field.configurationid]}>
          {(errors as any)[field.configurationid]?.message as string}
        </FormHelperText>
      </Box>
    );
  }

  // Simple format - dropdown with value/label pairs
  const hasValueLabelFormat = queryData.length > 0 && 
    queryData[0].hasOwnProperty('value') && 
    queryData[0].hasOwnProperty('label');

  if (hasValueLabelFormat) {
    return (
      <>
        <InputLabel>{getFieldLabel(field.label_key)}</InputLabel>
        <Controller
          name={field.configurationid.toString()}
          control={control}
          render={({ field: controllerField }) => (
            <Select
              {...controllerField}
              value={controllerField.value || ''}
              label={getFieldLabel(field.label_key)}
              disabled={!isFieldEditable(field)}
            >
              <MenuItem value="">
                <em>{t('mdm.none')}</em>
              </MenuItem>
              {queryData.map((item: any, index: number) => (
                <MenuItem key={item.value || index} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        <FormHelperText error={!!(errors as any)[field.configurationid]}>
          {(errors as any)[field.configurationid]?.message as string}
        </FormHelperText>
      </>
    );
  }

  // Fallback - serialize as JSON
  return (
    <>
      <InputLabel>{getFieldLabel(field.label_key)}</InputLabel>
      <Controller
        name={field.configurationid.toString()}
        control={control}
        render={({ field: controllerField }) => (
          <Select
            {...controllerField}
            value={controllerField.value || ''}
            label={getFieldLabel(field.label_key)}
            disabled={!isFieldEditable(field)}
          >
            <MenuItem value="">
              <em>{t('mdm.none')}</em>
            </MenuItem>
            {queryData.map((item: any, index: number) => {
              const jsonValue = JSON.stringify(item);
              const displayValue = Object.values(item).join(' - ');
              return (
                <MenuItem key={index} value={jsonValue}>
                  {displayValue}
                </MenuItem>
              );
            })}
          </Select>
        )}
      />
      <FormHelperText error={!!(errors as any)[field.configurationid]}>
        {(errors as any)[field.configurationid]?.message as string}
      </FormHelperText>
    </>
  );
}

// tree view sidebar (nav)
const TreeViewItem = ({
  node,
  selectedPath,
  onSelect,
  level = 0
}: {
  node: ScopeNode;
  selectedPath: string;
  onSelect: (path: string) => void;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedPath === node.path;
  const theme = useTheme();

  const handleClick = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    } else {
      onSelect(node.path);
    }
  };

  return (
    <Box>
      <ListItemButton
        onClick={handleClick}
        selected={isSelected}
        sx={{
          pl: 2 + level * 2,
          py: 1,
          minHeight: 40,
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              sx={{ mr: 1, p: 0.5 }}
            >
              {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          )}
          <ListItemText
            primary={node.name}
            primaryTypographyProps={{
              fontSize: node.children && node.children.length > 0 ? '1.25rem' : '1rem',
              fontWeight: isSelected ? 600 : (node.children && node.children.length > 0 ? 500 : 400),
            }}
          />
        </Box>
      </ListItemButton>
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children!.map((child, index) => (
              <TreeViewItem
                key={child.path}
                node={child}
                selectedPath={selectedPath}
                onSelect={onSelect}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
};

function buildYupSchema(fields: ConfigItem[], t: any) {
  const shape: any = {};

  fields.forEach((field: ConfigItem) => {
    let validator;

    switch (field.datatype) {
      case 'text':
      case 'string':
        validator = yup.string();
        break;
      case 'number':
        validator = yup.number();

        if (field.validation_schema) {
          try {
            const schema = JSON.parse(field.validation_schema);
            if (schema.min !== undefined) {
              validator = validator.min(schema.min, t('validation.minValue', { min: schema.min }));
            }
            if (schema.max !== undefined) {
              validator = validator.max(schema.max, t('validation.maxValue', { max: schema.max }));
            }
          } catch (e) {
            console.warn('Invalid validation schema for', field.configurationid);
          }
        }
        break;
      case 'checkbox':
        validator = yup.boolean();
        break;
      case 'selection':
        validator = yup.string();

        if (field.options_json) {
          try {
            const options = JSON.parse(field.options_json);
            const allowedValues = options.map((opt: any) => opt.value);
            const allowedValuesWithEmpty = ['', ...allowedValues];
            validator = validator.oneOf(allowedValuesWithEmpty, t('validation.invalidSelection'));
          } catch (e) {
            console.warn('Invalid options_json for', field.configurationid);
          }
        }
        break;
      case 'multiselection':
        validator = yup.array().of(yup.string());
        break;
      case 'json':
        validator = yup.string();
        
        if (field.options_json) {
          validator = validator.test('valid-json-entries', t('validation.invalidJson'), function(value) {
            if (!value) return true; // allow empty if not required
            try {
              const parsed = JSON.parse(value);
              if (!Array.isArray(parsed)) return false;
              return true;
            } catch {
              return false;
            }
          });
        }
        break;
      default:
        validator = yup.mixed();
    }


    shape[field.configurationid] = validator;
  });

  return yup.object().shape(shape);
}

function isFieldVisible(field: ConfigItem, parentValue: any, isParentVisible: boolean = true): boolean {
  // If no parent, field is visible by default
  if (!field.parent_id || !field.parent_condition) {
    return true;
  }

  // If parent is not visible, this field should not be visible either
  if (!isParentVisible) {
    return false;
  }

  try {
    const condition = JSON.parse(field.parent_condition);

    // Equals condition
    if (condition.equals !== undefined) {
      return parentValue === condition.equals;
    }

    // Not equals condition
    if (condition.notEquals !== undefined) {
      return parentValue !== condition.notEquals;
    }

    // Like condition (string contains)
    if (condition.like !== undefined) {
      const parentStr = String(parentValue || '').toLowerCase();
      const conditionStr = String(condition.like).toLowerCase();
      return parentStr.includes(conditionStr);
    }

    // Not like condition
    if (condition.notLike !== undefined) {
      const parentStr = String(parentValue || '').toLowerCase();
      const conditionStr = String(condition.notLike).toLowerCase();
      return !parentStr.includes(conditionStr);
    }

    // In condition (value is in array)
    if (condition.in !== undefined && Array.isArray(condition.in)) {
      return condition.in.includes(parentValue);
    }

    // Not in condition (value is not in array)
    if (condition.notIn !== undefined && Array.isArray(condition.notIn)) {
      return !condition.notIn.includes(parentValue);
    }

    // Greater than condition (for numbers)
    if (condition.greaterThan !== undefined) {
      const parentNum = Number(parentValue);
      const conditionNum = Number(condition.greaterThan);
      return !isNaN(parentNum) && !isNaN(conditionNum) && parentNum > conditionNum;
    }

    // Less than condition (for numbers)
    if (condition.lessThan !== undefined) {
      const parentNum = Number(parentValue);
      const conditionNum = Number(condition.lessThan);
      return !isNaN(parentNum) && !isNaN(conditionNum) && parentNum < conditionNum;
    }

    // Greater than or equal condition
    if (condition.greaterThanOrEqual !== undefined) {
      const parentNum = Number(parentValue);
      const conditionNum = Number(condition.greaterThanOrEqual);
      return !isNaN(parentNum) && !isNaN(conditionNum) && parentNum >= conditionNum;
    }

    // Less than or equal condition
    if (condition.lessThanOrEqual !== undefined) {
      const parentNum = Number(parentValue);
      const conditionNum = Number(condition.lessThanOrEqual);
      return !isNaN(parentNum) && !isNaN(conditionNum) && parentNum <= conditionNum;
    }

    // Is empty condition
    if (condition.isEmpty === true) {
      return parentValue === '' || parentValue === null || parentValue === undefined ||
        (Array.isArray(parentValue) && parentValue.length === 0);
    }

    // Is not empty condition
    if (condition.isNotEmpty === true) {
      return parentValue !== '' && parentValue !== null && parentValue !== undefined &&
        !(Array.isArray(parentValue) && parentValue.length === 0);
    }

    // If no condition matches, default to visible
    return true;
  } catch (e) {
    console.warn('Invalid parent condition for', field.configurationid);
    return true;
  }
}

function processFieldValue(field: ConfigItem, rawValue: any): any {
  if (!rawValue && rawValue !== 0 && rawValue !== false) {
    switch (field.datatype) {
      case 'multiselection': return [];
      case 'checkbox': return false;
      case 'number': return 0;
      case 'json': return '';
      default: return '';
    }
  }

  switch (field.datatype) {
    case 'multiselection':
      if (typeof rawValue === 'string') {
        try {
          const parsed = JSON.parse(rawValue);
          return Array.isArray(parsed) ? parsed : [rawValue];
        } catch {
          return rawValue.split(',').map((v: string) => v.trim());
        }
      }
      return Array.isArray(rawValue) ? rawValue : [];
    case 'checkbox':
      return rawValue === 'true' || rawValue === '1' || rawValue === true;
    case 'number':
      return Number(rawValue);
    default:
      return rawValue || '';
  }
}

function buildFilteredScopeTree(configData: ConfigItem[], navSearch: string, getFieldLabel: (labelKey: string) => string): ScopeNode[] {
  const search = navSearch.trim().toLowerCase();
  if (!search) return [];

  const isHardcodedScopeMatching = (scope: { path: string, searchTerms: string[] }) => {
    const scopeName = scope.path.split('/').pop() || '';
    return scopeName.toLowerCase().includes(search) ||
      scope.searchTerms.some(term => term.toLowerCase().includes(search));
  };

  // find all config items matching the string
  const matchingItems = configData.filter(item => {
    // check in keys
    const keyMatch =
      (item.label_key && item.label_key.toLowerCase().includes(search)) ||
      (item.key && item.key.toLowerCase().includes(search)) ||
      (item.description_key && item.description_key.toLowerCase().includes(search));

    // check in translations if we have a label_key
    const translationMatch = item.label_key ?
      getFieldLabel(item.label_key).toLowerCase().includes(search) : false;

    return keyMatch || translationMatch;
  });

  if (matchingItems.length === 0 && !HARDCODED_SCOPES.some(isHardcodedScopeMatching)) {
    return [];
  }

  // get all matching scope_paths
  const matchingPaths = new Set<string>();

  // add regular config paths
  matchingItems.forEach(item => {
    const parts = item.scope_path.split('/');
    let path = '';
    parts.forEach((part) => {
      path = path ? path + '/' + part : part;
      matchingPaths.add(path);
    });
  });

  // add matching hardcoded scopes
  HARDCODED_SCOPES.forEach(scope => {
    if (isHardcodedScopeMatching(scope)) {
      matchingPaths.add(scope.path);
      matchingPaths.add(scope.parentPath);
    }
  });

  // build the filtered tree
  const scopeMap = new Map<string, ScopeNode>();
  configData.forEach(item => {
    const scopePath = item.scope_path;
    const pathParts = scopePath.split('/');
    let currentPath = '';
    pathParts.forEach((part, index) => {
      const parentPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      if (!scopeMap.has(currentPath)) {
        scopeMap.set(currentPath, {
          name: part,
          path: currentPath,
          children: [],
          isLeaf: index === pathParts.length - 1
        });
      }
      if (parentPath && scopeMap.has(parentPath)) {
        const parent = scopeMap.get(parentPath)!;
        const child = scopeMap.get(currentPath)!;
        if (!parent.children?.some((c: ScopeNode) => c.path === child.path)) {
          (parent.children ?? []).push(child);
        }
      }
    });
  });
  // add hardcoded scopes
  HARDCODED_SCOPES.forEach(scope => {
    if (!scopeMap.has(scope.parentPath)) {
      scopeMap.set(scope.parentPath, {
        name: scope.parentPath.split('/').pop() || scope.parentPath,
        path: scope.parentPath,
        children: [],
        isLeaf: false
      });
    }

    scopeMap.set(scope.path, {
      name: scope.path.split('/').pop() || scope.path,
      path: scope.path,
      children: [],
      isLeaf: true
    });

    const parentNode = scopeMap.get(scope.parentPath)!;
    const childNode = scopeMap.get(scope.path)!;
    if (!parentNode.children?.some((c: ScopeNode) => c.path === childNode.path)) {
      (parentNode.children ?? []).push(childNode);
    }
  });

  // only keep nodes of matchingPaths
  const filteredNodes: ScopeNode[] = [];
  scopeMap.forEach(node => {
    if (!matchingPaths.has(node.path)) return;
    const parentPath = node.path.includes('/') ? node.path.substring(0, node.path.lastIndexOf('/')) : null;
    if (!parentPath || !matchingPaths.has(parentPath)) {
      filteredNodes.push(node);
    }
    node.children = (node.children ?? []).filter((child: ScopeNode) => matchingPaths.has(child.path));
  });
  return filteredNodes;
}

function useSearchAndAutoSwitch(
  navSearch: string,
  selectedScope: string,
  setSelectedScope: (scope: string) => void,
  filteredScopeTree: ScopeNode[],
  filteredFieldsForScope: ConfigItem[],
  getFieldLabel: (labelKey: string) => string
) {
  useEffect(() => {
    if (!navSearch.trim() || filteredScopeTree.length === 0) return;

    const search = navSearch.trim().toLowerCase();
    const isHardcodedScopeMatching = (scope: { path: string, searchTerms: string[] }) => {
      const scopeName = scope.path.split('/').pop() || '';
      return scopeName.toLowerCase().includes(search) ||
        scope.searchTerms.some(term => term.toLowerCase().includes(search));
    };

    // skip auto switching if current scope already matches search
    const isCurrentScopeHardcodedAndMatching = HARDCODED_SCOPES.some(scope =>
      scope.path === selectedScope && isHardcodedScopeMatching(scope)
    );

    if (isCurrentScopeHardcodedAndMatching) return;

    // check if current scope has any matching items
    const currentScopeItems = filteredFieldsForScope.filter(item =>
      item.scope_path === selectedScope
    );

    if (currentScopeItems.length > 0) return;

    // find scope to switch to
    const matchingHardcodedScope = HARDCODED_SCOPES.find(isHardcodedScopeMatching);
    if (matchingHardcodedScope) {
      setSelectedScope(matchingHardcodedScope.path);
      return;
    }

    const findScopeWithItems = (nodes: ScopeNode[]): string | null => {
      for (const node of nodes) {
        if (filteredFieldsForScope.some(item => item.scope_path === node.path)) {
          return node.path;
        }

        if (node.children?.length) {
          const childMatch = findScopeWithItems(node.children);
          if (childMatch) return childMatch;
        }
      }
      return null;
    };

    const firstScopeWithItems = findScopeWithItems(filteredScopeTree);
    if (firstScopeWithItems && firstScopeWithItems !== selectedScope) {
      setSelectedScope(firstScopeWithItems);
    }
  }, [navSearch, selectedScope, setSelectedScope, filteredScopeTree, filteredFieldsForScope, getFieldLabel]);
}

function MdmConfig() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [triggerMessage] = useToast();
  const theme = useTheme();

  const [configData, setConfigData] = useState<ConfigItem[]>([]);
  const [currentValues, setCurrentValues] = useState<{ [key: string]: any }>({});
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedScope, setSelectedScope] = useState<string>('');
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = useState<{ [key: string]: boolean }>({});
  const [navSearch, setNavSearch] = useState('');
  const userState = useAppStoreSelector((state) => state.user);
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [activeOptions, setActiveOptions] = useState<OptionsInterface[]>([]);
  const [optionsModalLabel, setOptionsModalLabel] = useState('');

  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressStatusEndpoint, setProgressStatusEndpoint] = useState('/api/mdm/dam/v1/sync/status');

  const [sidebarVisible, setSidebarVisible] = useState(true);

  const getFieldLabel = useCallback((labelKey: string) => {
    const translation = t(labelKey);
    return translation !== labelKey ? translation : labelKey;
  }, [t]);

  // confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    action: '' as 'back' | 'scope',
    newScope: ''
  });

  const handleBackClick = () => {
    if (isDirty) {
      setConfirmationDialog({
        open: true,
        action: 'back',
        newScope: ''
      });
    } else {
      navigate('/');
    }
  };

  const handleScopeChange = (newScope: string) => {
    if (isDirty) {
      setConfirmationDialog({
        open: true,
        action: 'scope',
        newScope: newScope
      });
    } else {
      setSelectedScope(newScope);
      // check if the new scope is in hardcoded scopes
      const isHardcodedScope = HARDCODED_SCOPES.some(scope => scope.path === newScope);
      if (isHardcodedScope) {
        // reset form dirty state when switching to hardcoded scope
        reset(getValues());
      }
    }
  };

  const handleConfirmationDialogClose = () => {
    setConfirmationDialog({
      open: false,
      action: 'back',
      newScope: ''
    });
  };

  const handleConfirmAction = () => {
    if (confirmationDialog.action === 'back') {
      navigate('/');
    } else if (confirmationDialog.action === 'scope') {
      setSelectedScope(confirmationDialog.newScope);
      // check if the new scope is in hardcoded scopes
      const isHardcodedScope = HARDCODED_SCOPES.some(scope => scope.path === confirmationDialog.newScope);
      if (isHardcodedScope) {
        reset(getValues());
      }
    }
    handleConfirmationDialogClose();
  };

  // helper function to toggle sensitive field visibility
  const toggleSensitiveFieldVisibility = (fieldId: string) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  };

  const openStatusModal = (endpoint: string) => {
    setProgressStatusEndpoint(endpoint);
    setProgressModalOpen(true);
  };

  const openOptionsModal = (
    options: OptionsInterface[],
    label: string
  ) => {
    setActiveOptions(options);
    setOptionsModalLabel(label);
    setOptionsModalOpen(true);
  };

  const syncOptions: OptionsInterface[] = [
    {
      type: 'option',
      label: t('mdm.fullSync'),
      description: t('mdm.fullSyncDescription'),
      style: 'primary',
      icon: <SyncIcon />,
      endpoint: '/api/mdm/dam/v1/sync',
      confirmationRequired: false
    },
    {
      type: 'option',
      label: t('mdm.backup'),
      description: t('mdm.backupDescription'),
      style: 'primary',
      icon: <BackupIcon />,
      endpoint: '/api/mdm/dam/v1/sync/backup',
      confirmationRequired: false
    },
    {
      type: 'option',
      label: t('mdm.rebuild'),
      description: t('mdm.rebuildDescription'),
      style: 'warning',
      icon: <ConstructionIcon />,
      endpoint: '/api/mdm/dam/v1/sync/rebuild',
      confirmationRequired: true
    },
    {
      type: 'divider'
    },
    {
      type: 'option',
      label: t('mdm.status'),
      description: t('mdm.statusDescription'),
      style: 'primary',
      icon: <InfoIcon />,
      endpoint: 'STATUS',
      confirmationRequired: false,
      onClick: () => openStatusModal('/api/mdm/dam/v1/sync/status')
    }
  ];

  const previewOptions: OptionsInterface[] = [
    {
      type: 'option',
      label: t('mdm.default'),
      description: t('mdm.defaultDescription'),
      style: 'primary',
      icon: <SlideshowIcon />,
      endpoint: '/api/mdm/dam/v1/previews/generate?force=false',
      confirmationRequired: false
    },
    {
      type: 'option',
      label: t('mdm.forceMode'),
      description: t('mdm.forceModeDescription'),
      style: 'warning',
      icon: <RestartAltIcon />,
      endpoint: '/api/mdm/dam/v1/previews/generate?force=true',
      confirmationRequired: true
    },
    {
      type: 'divider'
    },
    {
      type: 'option',
      label: t('mdm.previewStatus'),
      description: t('mdm.previewStatusDescription'),
      style: 'primary',
      icon: <InfoIcon />,
      endpoint: 'STATUS',
      confirmationRequired: false,
      onClick: () => openStatusModal('/api/mdm/dam/v1/previews/status')
    }
  ];

  /* const progressStatus: ProgressInterface | null = {
    status: "running",
    type: "sync",
    startTime: new Date().toISOString(),
    progress: {
      total: 100,
      processed: 35,
      succeeded: 34,
      failed: 1,
      percentage: 35,
      currentFile: "/path/file.jpg",
      rate: "120/min",
      estimatedTimeRemaining: "5 minutes",
    },
  }; */

  // fetch config data and languages from API (only once)
  useEffect(() => {
    const fetchConfigAndLanguages = async () => {
      try {
        setLoading(true);
        setError(null);

        const statusResponse = await axios.get('/api/mdm/config/status');
        
        if (!statusResponse.data?.mdmConfigured) {
          setError('MDM database is not configured. Please contact a developer.');
          setLoading(false);
          return;
        }

        const [configResponse, languagesResponse] = await Promise.all([
          axios.get('/api/mdm/config/getData'),
          axios.get('/api/mdm/config/getLanguages')
        ]);

        if (configResponse.data && configResponse.data.success && configResponse.data.data && Array.isArray(configResponse.data.data)) {
          const items = configResponse.data.data as ConfigItem[];
          setConfigData(items);

          if (items.length > 0) {
            // set initial scope to the first available scope_path
            const firstItem = items[0];
            setSelectedScope(firstItem.scope_path);
          }
        } else {
          throw new Error('Failed to fetch configuration data - invalid response format');
        }

        if (languagesResponse.data && languagesResponse.data.success && languagesResponse.data.data) {
          setLanguages(languagesResponse.data.data);
        }

      } catch (err: any) {
        if (err.response?.status === 401) {
          navigate(`/auth?redirect=${encodeURIComponent('/mdm-config')}`);
        } else if (err.response?.status === 503) {
          setError('MDM database is not configured or not accessible. Please contact a developer.');
        } else {
          setError(err.message || 'Failed to load configuration');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConfigAndLanguages();
  }, []);

  // fetch current values whenever scope changes
  useEffect(() => {
    if (!selectedScope) return; // don't fetch if no scope selected yet
    if (error) return; // don't fetch if there's already an error (for example, MDM not configured)

    const fetchCurrentValues = async () => {
      try {
        const valuesResponse = await axios.get('/api/mdm/config/getCurrentValues');

        if (valuesResponse.data && valuesResponse.data.success && valuesResponse.data.data) {
          setCurrentValues(valuesResponse.data.data);
        }

      } catch (err: any) {
        console.warn('Failed to fetch current values:', err.message);
        // don'tt set error state here since this is secondary data
      }
    };

    fetchCurrentValues();
  }, [selectedScope]);

  // build hierarchical scope tree
  const scopeTree = useMemo(() => {
    const scopeMap = new Map<string, ScopeNode>();

    configData.forEach(item => {
      const scopePath = item.scope_path;
      const pathParts = scopePath.split('/');

      let currentPath = '';
      pathParts.forEach((part: string, index: number) => {
        const parentPath = currentPath;
        currentPath = currentPath ? `${currentPath}/${part}` : part;

        if (!scopeMap.has(currentPath)) {
          scopeMap.set(currentPath, {
            name: part,
            path: currentPath,
            children: [],
            isLeaf: index === pathParts.length - 1
          });
        }

        if (parentPath && scopeMap.has(parentPath)) {
          const parent = scopeMap.get(parentPath)!;
          const child = scopeMap.get(currentPath)!;
          if (!parent.children!.some(c => c.path === child.path)) {
            parent.children!.push(child);
          }
        }
      });
    });

    // add hardcoded scopes under their parent paths
    HARDCODED_SCOPES.forEach(scope => {
      // add parent scope if it doesnt exist
      if (!scopeMap.has(scope.parentPath)) {
        scopeMap.set(scope.parentPath, {
          name: scope.parentPath.split('/').pop() || scope.parentPath,
          path: scope.parentPath,
          children: [],
          isLeaf: false
        });
      }

      scopeMap.set(scope.path, {
        name: scope.path.split('/').pop() || scope.path,
        path: scope.path,
        children: [],
        isLeaf: true
      });

      const parentNode = scopeMap.get(scope.parentPath)!;
      const childNode = scopeMap.get(scope.path)!;
      if (!parentNode.children!.some(c => c.path === childNode.path)) {
        parentNode.children!.push(childNode);
      }
    });

    const rootNodes: ScopeNode[] = [];
    scopeMap.forEach(node => {
      const hasParent = Array.from(scopeMap.values()).some(n =>
        n.children && n.children.some(c => c.path === node.path)
      );
      if (!hasParent) {
        rootNodes.push(node);
      }
    });

    return rootNodes.sort((a, b) => a.name.localeCompare(b.name));
  }, [configData]);

  // build hierarchical scope tree (filtered if searching)
  const filteredScopeTree = useMemo(() => {
    return navSearch.trim()
      ? buildFilteredScopeTree(configData, navSearch, getFieldLabel)
      : scopeTree;
  }, [navSearch, configData, scopeTree, getFieldLabel]);

  // helper to create map of visibility (parents, children, ...)
  const calculateFieldVisibility = (fields: ConfigItem[], parentValuesMap: { [key: number]: any }) => {
    const visibilityMap: { [key: number]: boolean } = {};

    const sortedFields = [...fields].sort((a, b) => {
      if (!a.parent_id && b.parent_id) return -1;
      if (a.parent_id && !b.parent_id) return 1;
      return a.sort_order - b.sort_order;
    });

    sortedFields.forEach((field: ConfigItem) => {
      if (!field.parent_id) {
        visibilityMap[field.configurationid] = true;
      } else {
        // check parent visibility and condition
        const parentValue = parentValuesMap[field.parent_id];
        const isParentVisible = visibilityMap[field.parent_id] ?? true;
        visibilityMap[field.configurationid] = isFieldVisible(field, parentValue, isParentVisible);
      }
    });

    return visibilityMap;
  };

  // get fields for selected scope
  const fieldsForScope = useMemo(() => {
    return configData
      .filter(item => item.scope_path === selectedScope)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [configData, selectedScope]);

  const schema = useMemo(() => buildYupSchema(fieldsForScope, t), [fieldsForScope, t]);

  const initialDefaults = useMemo(() => {
    const defaults: any = {};

    fieldsForScope.forEach((field: ConfigItem) => {
      if (field.is_translatable === 1 || field.is_translatable === true) {
        // handle translatable fields
        languages.forEach((language: Language) => {
          const fieldKey = `${field.configurationid}_lang_${language.LangCode}`;
          const rawValue = currentValues[fieldKey] !== undefined ?
            currentValues[fieldKey] : field.default_value;

          defaults[fieldKey] = rawValue || '';
        });
      } else {
        // handle regular fields
        const rawValue = currentValues[field.configurationid] !== undefined ?
          currentValues[field.configurationid] : field.default_value;

        defaults[field.configurationid] = processFieldValue(field, rawValue);
      }
    });

    return defaults;
  }, [fieldsForScope, currentValues, languages]);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    getValues,
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: initialDefaults,
  });

  // find all parent fields (fields that have children referencing them)
  const parentFields = useMemo(() => {
    const parentIds = new Set(fieldsForScope.filter((field: ConfigItem) => field.parent_id).map((field: ConfigItem) => field.parent_id));
    return fieldsForScope.filter((field: ConfigItem) => parentIds.has(field.configurationid));
  }, [fieldsForScope]);

  // get default language for translatable parent fields
  const defaultLanguage = useMemo(() => {
    return languages.find((lang: Language) => lang.IsDefault === 1) || languages[0];
  }, [languages]);

  const parentKeys = useMemo(() => {
    return parentFields.map((field: ConfigItem) => {
      // for translatable parent fields, watch the default language input
      if ((field.is_translatable === 1 || field.is_translatable === true) && defaultLanguage) {
        return `${field.configurationid}_lang_${defaultLanguage.LangCode}`;
      }
      // for regular parent fields, watch the field itself
      return field.configurationid.toString();
    });
  }, [parentFields, defaultLanguage]);
  const parentFormValues = watch(parentKeys);

  const parentValuesMap = useMemo(() => {
    const newMap: { [key: number]: any } = {};
    parentFields.forEach((field: ConfigItem, index: number) => {
      newMap[field.configurationid] = parentFormValues[index];
    });
    return newMap;
  }, [parentFields, parentFormValues]);

  const fieldVisibilityMap = useMemo(() => {
    return calculateFieldVisibility(fieldsForScope, parentValuesMap);
  }, [fieldsForScope, parentValuesMap]);

  // get fields for selected scope (filtered if searching)
  const filteredFieldsForScope = useMemo(() => {
    let items: ConfigItem[];

    if (navSearch.trim()) {
      const search = navSearch.trim().toLowerCase();
      items = configData.filter((item: ConfigItem) => {
        // check in keys
        const keyMatch =
          (item.label_key && item.label_key.toLowerCase().includes(search)) ||
          (item.key && item.key.toLowerCase().includes(search)) ||
          (item.description_key && item.description_key.toLowerCase().includes(search));

        // check in translations if we have a label_key
        const translationMatch = item.label_key ?
          getFieldLabel(item.label_key).toLowerCase().includes(search) : false;

        return keyMatch || translationMatch;
      });

    } else {
      // if not searching, only show fields from selected scope
      items = configData.filter((item: ConfigItem) => item.scope_path === selectedScope);
      items = items.filter((item: ConfigItem) => fieldVisibilityMap[item.configurationid]);
    }

    return items.sort((a: ConfigItem, b: ConfigItem) => a.sort_order - b.sort_order);
  }, [configData, selectedScope, navSearch, fieldVisibilityMap, getFieldLabel]);

  // use the custom hook for search and auto switching
  useSearchAndAutoSwitch(
    navSearch,
    selectedScope,
    setSelectedScope,
    filteredScopeTree,
    filteredFieldsForScope,
    getFieldLabel
  );

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      const username = userState.data.user?.username || "unknown";

      // Create language mapping for multi-language fields
      const languageMap: { [key: string]: number } = {};
      languages.forEach(language => {
        languageMap[language.LangCode] = language.LanguageID;
      });

      const response = await axios.post('/api/mdm/config/saveData', {
        data,
        updated_by: username,
        languageMap
      });

      if (response.data && response.data.success) {
        triggerMessage('Configuration saved successfully', false);
        // Reset form dirty state after successful save by resetting with current values
        const currentFormValues = getValues();
        reset(currentFormValues);
      } else {
        throw new Error('Failed to save configuration - invalid response format');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
      triggerMessage(err.message || 'Failed to save configuration', true);
    } finally {
      setLoading(false);
    }
  };

  // reset form when scope changes
  useEffect(() => {
    if (fieldsForScope.length > 0) {
      reset(initialDefaults);
    }
  }, [fieldsForScope, reset, initialDefaults]);


  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        minHeight: '67px'
      }}>
        <Breadcrumbs
          separator={<NavigateNextIcon
            sx={{ fontSize: '0.75rem' }}
          />}
          aria-label="breadcrumb"
          sx={{ ml: 4 }}
        >
          <Link
            underline="hover"
            color={theme.palette.text.secondary}
            sx={{ fontWeight: 450, fontSize: '0.875rem', cursor: 'pointer' }}
            onClick={handleBackClick}
          >
            ← {t('mdm.backToDashboard')}
          </Link>
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {t('mdm.configTitle')}
          </Typography>
        </Breadcrumbs>
      </Box>


      <CssBaseline />

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={confirmationDialog.open}
        onClose={handleConfirmationDialogClose}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
      >
        <DialogTitle id="unsaved-changes-dialog-title">
          {t('mdm.unsavedChangesTitle')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('mdm.unsavedChangesWarning')}
          </Alert>
          <DialogContentText id="unsaved-changes-dialog-description">
            {confirmationDialog.action === 'back'
              ? t('mdm.unsavedChangesLeavePage')
              : t('mdm.unsavedChangesSwitchScope')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmationDialogClose}>
            {t('mdm.cancel')}
          </Button>
          <Button onClick={handleConfirmAction} color="primary" variant="contained">
            {t('mdm.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            {t('mdm.loadingConfiguration')}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Box sx={{ display: 'flex', flex: 1, height: '100%' }}>
          {/* Sidebar */}

          <Box sx={{ width: sidebarVisible ? 300 : 0, transition: 'width 0.3s ease' }}>
            <Box
              sx={{
                position: 'relative',
                height: 'calc(100vh - 120px)',
                backgroundColor: theme.palette.background.alt,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderRadius: 0,
                overflowX: 'hidden',
                transition: 'width 0.3s ease, opacity 0.3s ease',
                width: sidebarVisible ? 300 : 0,
                opacity: sidebarVisible ? 1 : 0,
              }}
            >
              <TextField
                value={navSearch}
                onChange={e => setNavSearch(e.target.value)}
                placeholder={t('search')}
                size="small"
                fullWidth
                sx={{
                  my: 2,
                  mx: 'auto',
                  display: 'block',
                  width: '80%',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    '& fieldset': {
                      borderColor: theme.palette.divider
                    },
                  },
                  '& input::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 0.8,
                  }
                }}
                variant="outlined"
              />

              <Divider sx={{
                mt: 2,
                width: '90%',
                mx: 'auto',
                display: 'block',
                borderColor: theme.palette.divider
              }} />

              <List>
                {filteredScopeTree && filteredScopeTree.map((node) => (
                  <TreeViewItem
                    key={node.path}
                    node={node}
                    selectedPath={selectedScope}
                    onSelect={handleScopeChange}
                  />
                ))}
              </List>
            </Box>
          </Box>
          {/* Main Content */}

          <Box sx={{ flex: 1, backgroundColor: theme.palette.background.default }}>
            <IconButton
              onClick={() => setSidebarVisible(!sidebarVisible)}
              sx={{
                position: 'absolute',
                transform: 'translateY(-50%)',
                mt: '30px',
                ml: '10px',
                zIndex: 20,
                backgroundColor: theme.palette.action.hover,
                boxShadow: 2,
                borderRadius: '50%',
                '&:hover': { backgroundColor: theme.palette.background.paper },
              }}
            >
              {sidebarVisible ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
            {selectedScope && HARDCODED_SCOPES.find(s => s.path === selectedScope) ? (
              <Box sx={{ p: 3 }}>
                {/* Hardcoded components */}
                {selectedScope === 'General/Languages' && <LanguagesTable languages={languages} />}
                {selectedScope === 'General/Translations' && <TranslationsTable />}
              </Box>
            ) : selectedScope && fieldsForScope.length > 0 ? (
              <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 3 }}>
                {/* Show message if no visible fields */}
                {filteredFieldsForScope.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      {navSearch.trim()
                        ? t('mdm.noVisibleFieldsForSearch', { search: navSearch })
                        : t('mdm.noVisibleFields')
                      }
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 3, mt: '-8px', ml: '40px' }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        {navSearch.trim()
                          ? t('mdm.searchResults', { search: navSearch })
                          : selectedScope.split('/').pop() || selectedScope
                        }
                      </Typography>
                    </Box>

                    {/* Group fields by ui_group */}
                    {Object.entries(
                      filteredFieldsForScope
                        .filter(field => field && field.configurationid)
                        .reduce((groups: { [key: string]: ConfigItem[] }, field) => {
                          const group = field.ui_group || '__no_group__';
                          if (!groups[group]) groups[group] = [];
                          groups[group].push(field);
                          return groups;
                        }, {})
                    )
                      .sort(([groupA], [groupB]) => {
                        // put __no_group__ first
                        if (groupA === '__no_group__') return -1;
                        if (groupB === '__no_group__') return 1;
                        return 0;
                      })
                      .sort(([groupA, groupFieldsA], [groupB, groupFieldsB]) => {
                        // skip sort for no_group items
                        if (groupA === '__no_group__' || groupB === '__no_group__') return 0;
                        // sort other groups by minimum sort_order
                        const minOrderA = Math.min(...groupFieldsA.map(f => f.sort_order));
                        const minOrderB = Math.min(...groupFieldsB.map(f => f.sort_order));
                        return minOrderA - minOrderB;
                      })
                      .map(([groupName, groupFields]) => {
                        const isUngrouped = groupName === '__no_group__';
                        const content = (
                          <Box>
                            {groupFields
                              .sort((a, b) => a.sort_order - b.sort_order)
                              .filter(field => fieldVisibilityMap[field.configurationid])
                              .map((field: ConfigItem) => {

                                  let options: any[] = [];
                                  if (field.options_json) {
                                    try {
                                      options = JSON.parse(field.options_json);
                                    } catch (e) {
                                    }
                                  }

                                  return (
                                    <FormControl fullWidth margin="normal" key={field.configurationid}>
                                      {/* Multi-language fields */}
                                      {(field.is_translatable === 1 || field.is_translatable === true) ? (
                                        <MultiLanguageInput field={field} control={control} errors={errors} languages={languages} sensitiveFieldsVisible={sensitiveFieldsVisible} toggleSensitiveFieldVisibility={toggleSensitiveFieldVisibility} getFieldLabel={getFieldLabel} />
                                      ) : (
                                        <>
                                          {/* Text Input */}
                                          {(field.datatype === 'text' || field.datatype === 'string') && (
                                            <Controller
                                              name={field.configurationid.toString()}
                                              control={control}
                                              render={({ field: controllerField }) => {
                                                const isSensitive = isFieldSensitive(field);
                                                const isVisible = sensitiveFieldsVisible[field.configurationid.toString()] || !isSensitive;

                                                return (
                                                  <TextField
                                                    {...controllerField}
                                                    value={controllerField.value || ''}
                                                    label={getFieldLabel(field.label_key)}
                                                    disabled={!isFieldEditable(field)}
                                                    type={isSensitive && !isVisible ? 'password' : 'text'}
                                                    error={!!(errors as any)[field.configurationid]}
                                                    helperText={(errors as any)[field.configurationid]?.message as string}
                                                    InputProps={{
                                                      endAdornment: isSensitive ? (
                                                        <InputAdornment position="end">
                                                          <IconButton
                                                            onClick={() => toggleSensitiveFieldVisibility(field.configurationid.toString())}
                                                            edge="end"
                                                            size="small"
                                                          >
                                                            {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                          </IconButton>
                                                        </InputAdornment>
                                                      ) : undefined,
                                                    }}
                                                  />
                                                );
                                              }}
                                            />
                                          )}

                                          {/* Number Input */}
                                          {field.datatype === 'number' && (
                                            <Controller
                                              name={field.configurationid.toString()}
                                              control={control}
                                              render={({ field: controllerField }) => {
                                                const isSensitive = isFieldSensitive(field);
                                                const isVisible = sensitiveFieldsVisible[field.configurationid.toString()] || !isSensitive;

                                                return (
                                                  <TextField
                                                    {...controllerField}
                                                    value={controllerField.value ?? ''}
                                                    type="number"
                                                    label={getFieldLabel(field.label_key)}
                                                    disabled={!isFieldEditable(field)}
                                                    error={!!(errors as any)[field.configurationid]}
                                                    helperText={(errors as any)[field.configurationid]?.message as string}
                                                    InputProps={{
                                                      endAdornment: isSensitive ? (
                                                        <InputAdornment position="end">
                                                          <IconButton
                                                            onClick={() => toggleSensitiveFieldVisibility(field.configurationid.toString())}
                                                            edge="end"
                                                            size="small"
                                                          >
                                                            {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                          </IconButton>
                                                        </InputAdornment>
                                                      ) : undefined,
                                                    }}
                                                    sx={isSensitive && !isVisible ? {
                                                      '& input': {
                                                        WebkitTextSecurity: 'disc',
                                                        textSecurity: 'disc'
                                                      }
                                                    } : undefined}
                                                  />
                                                );
                                              }}
                                            />
                                          )}

                                          {/* Selection Dropdown */}
                                          {field.datatype === 'selection' && (
                                            <>
                                              <InputLabel>{getFieldLabel(field.label_key)}</InputLabel>
                                              <Controller
                                                name={field.configurationid.toString()}
                                                control={control}
                                                render={({ field: controllerField }) => (
                                                  <Select
                                                    {...controllerField}
                                                    value={controllerField.value || ''}
                                                    label={getFieldLabel(field.label_key)}
                                                    disabled={!isFieldEditable(field)}
                                                  >
                                                    {options.map((option: any) => (
                                                      <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                )}
                                              />
                                              <FormHelperText error={!!(errors as any)[field.configurationid]}>
                                                {(errors as any)[field.configurationid]?.message as string}
                                              </FormHelperText>
                                            </>
                                          )}

                                          {/* Multi-selection */}
                                          {field.datatype === 'multiselection' && (
                                            <>
                                              <InputLabel>{getFieldLabel(field.label_key)}</InputLabel>
                                              <Controller
                                                name={field.configurationid.toString()}
                                                control={control}
                                                render={({ field: controllerField }) => (
                                                  <Select
                                                    {...controllerField}
                                                    multiple
                                                    label={getFieldLabel(field.label_key)}
                                                    value={controllerField.value || []}
                                                    disabled={!isFieldEditable(field)}
                                                  >
                                                    {options.map((option: any) => (
                                                      <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                )}
                                              />
                                              <FormHelperText error={!!(errors as any)[field.configurationid]}>
                                                {(errors as any)[field.configurationid]?.message as string}
                                              </FormHelperText>
                                            </>
                                          )}

                                          {/* Checkbox */}
                                          {field.datatype === 'checkbox' && (
                                            <Controller
                                              name={field.configurationid.toString()}
                                              control={control}
                                              render={({ field: controllerField }) => (
                                                <FormGroup>
                                                  <FormControlLabel
                                                    control={
                                                      <Checkbox
                                                        {...controllerField}
                                                        checked={!!controllerField.value}
                                                        onChange={(e) => controllerField.onChange(e.target.checked)}
                                                        disabled={!isFieldEditable(field)}
                                                      />
                                                    }
                                                    label={getFieldLabel(field.label_key)}
                                                  />
                                                </FormGroup>
                                              )}
                                            />
                                          )}

                                          {/* JSON Field (options_json contains SQL query) */}
                                          {field.datatype === 'json' && (
                                            <JsonFieldInput
                                              field={field}
                                              control={control}
                                              errors={errors}
                                              getFieldLabel={getFieldLabel}
                                            />
                                          )}
                                        </>
                                      )}
                                    </FormControl>
                                  );
                              })}
                          </Box>
                        );

                        // render without accordion if no group
                        if (isUngrouped) {
                          return <Box key={groupName} sx={{ mb: 2 }}>{content}</Box>;
                        }

                        // render with accordion if grouped
                        return (
                          <Accordion key={groupName} defaultExpanded sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1" sx={{
                                fontWeight: 500,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                fontSize: '0.875rem',
                                letterSpacing: 0.5
                              }}>
                                {groupName.replace(/_/g, ' ')}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {content}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}

                    <Box sx={{ mt: 3, gap: 2, display: 'flex', flexDirection: 'row' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                      >
                        {t('mdm.saveConfiguration', { scope: selectedScope })}
                      </Button>
                      {selectedScope === 'DAM/Sync' && (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => openOptionsModal(syncOptions, t('mdm.syncOptions'))}
                        >
                          {t('mdm.syncOptions')}
                        </Button>
                      )}
                      {/* {selectedScope === 'DAM/Preview' && (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => openOptionsModal(previewOptions, t('mdm.previewOptions'))}
                        >
                          {t('mdm.previewOptions')}
                        </Button>
                      )} */}
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 6
                }}
              >
                <Typography variant="body1">
                  {selectedScope ? t('mdm.noConfigurationItems') : t('mdm.selectScopePrompt')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <OptionsModal
        open={optionsModalOpen}
        onClose={() => setOptionsModalOpen(false)}
        options={activeOptions}
        label={optionsModalLabel}
      />

      <ProgressModal
        open={progressModalOpen}
        statusEndpoint={progressStatusEndpoint}
        onClose={() => setProgressModalOpen(false)}
      />
    </Box >
  );
}

export default MdmConfig; 